import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useOutletContext, useParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import PuertaCerrada from "../components/funcionales/PuertaCerrada";
import Patente from "../components/funcionales/Patente";
import Select from "../components/inputs/SelectBase";
import QrLlave from "../components/llaves/QrLlave";
import { seccion } from "../components/layout/secciones";
import { anotarMovimiento, confirmacionesLlaves, movimientosLlaves } from "../services/llaves";
import { useRealtime } from "../services/store";
import {
  ENTREGA,
  RETIRA,
  confirmacionDe,
  demorada,
  estadoLlaves,
  hace,
  proximoMovimiento,
  quienEscanea,
  rutaEtiquetas,
  urlLlave,
} from "../shared/llaves";
import Loading from "./Loading";
import "./css/llaves.css";

/* LA LLAVE DE UNA UNIDAD: a donde lleva el QR pegado en el llavero (ticket
 * 522). El sereno escanea con la cámara del celular, y esta pantalla ya sabe
 * qué llave es y qué corresponde anotar: si está en portería, que se la
 * llevan; si está afuera, que vuelve. Queda un botón.
 *
 * Lo que se pide es lo mínimo que hace falta para que el registro sirva:
 *
 *   · QUIÉN SE LA LLEVA (o quién la trae). Al devolverla ya viene elegido el
 *     que la tenía: lo normal es que la traiga el mismo, y si no, se cambia;
 *   · el SERENO que la da. Se recuerda en el celular: el de la portería es
 *     casi siempre el mismo, y elegirse en cada llave es lo que hace que se
 *     deje de anotar.
 *
 * QUIÉN ESCANEA (Igna, 27/10/2026): si el que escanea entró al hub con su
 * propio usuario, la llave es para él (o la trae él) y no se le pregunta nada
 * (`quienEscanea`). Sólo desde la cuenta de portería se elige la persona y el
 * sereno, como arriba.
 *
 * ENTREGA O RECEPCIÓN: viene marcada la que corresponde según dónde está la
 * llave, pero se puede elegir la otra —un movimiento que no se anotó deja el
 * estado al revés, y quien está con la llave en la mano sabe qué pasa—.
 * «Entrega» es que la portería la da (se la llevan: RETIRA en `eventos`) y
 * «Recepción» que la recibe de vuelta (ENTREGA en `eventos`).
 *
 * La fecha y la hora las pone el sistema. Se guarda en `eventos` igual que el
 * formulario de siempre, así que aparece en los mismos listados. */

const RECORDAR_SERENO = "llaves.operador";

const leerSereno = () => {
  try {
    return localStorage.getItem(RECORDAR_SERENO) || null;
  } catch {
    return null;
  }
};

const guardarSereno = (v) => {
  try {
    if (v) localStorage.setItem(RECORDAR_SERENO, String(v));
  } catch {
    // Sin almacenamiento (modo privado) se elige cada vez; no es un error.
  }
};

const normalizar = (s) =>
  String(s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z ]/gi, " ")
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .sort()
    .join(" ");

const horaCorta = (d) =>
  d.toLocaleString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

const Llave = () => {
  const { interno: crudo } = useParams();
  const interno = decodeURIComponent(crudo || "").trim();
  const { permisos = null } = useOutletContext() || {};
  const permiso = seccion("llaves")?.permiso || "ver.porteria";
  const { fullUser } = useAuth() || {};
  const yo = quienEscanea(fullUser);
  // La llave escaneada abre para cualquiera que entró con su usuario: se la
  // anota a él mismo. Preguntar por otro (la portería) pide el permiso.
  const puede =
    permisos === null || Boolean(yo) || [].concat(permiso).some((p) => permisos.includes(p));
  const tractores = useRealtime("tractores") || [];
  const personas = useRealtime("personas") || [];
  const serenos = useRealtime("serenos") || [];

  const [movs, setMovs] = useState(null);
  const [confirmaciones, setConfirmaciones] = useState([]);
  const [error, setError] = useState(false);
  const [persona, setPersona] = useState(null);
  const [tipo, setTipo] = useState(null);
  const [operador, setOperador] = useState(leerSereno);
  const [detalle, setDetalle] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [resultado, setResultado] = useState(null);

  const traer = useCallback(async () => {
    try {
      setMovs(await movimientosLlaves());
      setError(false);
    } catch (e) {
      console.error("[Llaves] no se pudo consultar", e);
      setError(true);
    }
    // Sin las confirmaciones la llave se ve igual, sin la marca.
    try {
      setConfirmaciones(await confirmacionesLlaves());
    } catch (e) {
      console.error("[Llaves] no se pudieron leer las confirmaciones", e);
    }
  }, []);

  useEffect(() => {
    if (puede) traer();
  }, [traer, puede]);

  const est = useMemo(() => (movs ? estadoLlaves(movs)[interno] || null : null), [movs, interno]);
  const toca = tipo ?? proximoMovimiento(est);
  const conf = confirmacionDe(est, confirmaciones);

  // Al recibirla, el que la tenía ya viene elegido. Si escanea alguien con su
  // usuario, es él.
  useEffect(() => {
    if (yo) setPersona(yo);
    else if (toca === ENTREGA && est?.persona) setPersona(est.persona);
    else if (toca === RETIRA) setPersona(null);
  }, [yo, toca, est?.persona]);

  // Si el celular no recuerda sereno, y el que entró al hub es uno, es él.
  useEffect(() => {
    if (yo || operador || !fullUser || !serenos.length) return;
    const nombre = normalizar(`${fullUser.apellido || ""} ${fullUser.nombres || ""}`);
    const s = nombre && serenos.find((x) => normalizar(x.nombreCompleto) === nombre);
    if (s) setOperador(String(s.id));
  }, [yo, operador, fullUser, serenos]);

  const tractor = tractores.find((t) => String(t.id) === interno) || null;
  const nombreDe = (dni) =>
    personas.find((p) => String(p.id) === String(dni))?.nombreCompleto ||
    // El que escanea puede no estar en `personas`: su nombre es el del hub.
    (dni && String(dni) === yo && fullUser?.nombreCompleto) ||
    (dni ? `DNI ${dni}` : "—");

  const opcionesPersonas = useMemo(
    () => personas.map((p) => ({ value: String(p.id), label: p.nombreCompleto })),
    [personas],
  );
  const opcionesSerenos = useMemo(
    () => serenos.map((p) => ({ value: String(p.id), label: p.nombreCompleto })),
    [serenos],
  );

  if (permisos !== null && !puede) return <PuertaCerrada />;
  if (error && !movs) {
    return (
      <div className="panel-aviso"><h3>No se pudo consultar</h3></div>
    );
  }
  if (!movs) return <Loading />;

  const listo = Boolean(persona && (yo || operador));
  const anotar = async () => {
    if (!listo || guardando) return;
    setGuardando(true);
    setResultado(null);
    try {
      await anotarMovimiento({
        tipo: toca,
        interno,
        persona,
        operador: yo ? null : operador,
        detalle,
      });
      if (!yo) guardarSereno(operador);
      setTipo(null);
      setResultado({ ok: true, tipo: toca, persona, cuando: new Date() });
      setDetalle("");
      await traer();
    } catch (e) {
      console.error("[Llaves] no se pudo guardar", e);
      setResultado({ ok: false });
    } finally {
      setGuardando(false);
    }
  };

  const elegida = (ops, v) => ops.find((o) => o.value === String(v)) || null;

  return (
    <div className="docs llave">
      <div className="llave-cab">
        <h2 className="llave-interno">Llave del {interno}</h2>
        {tractor?.dominio && <Patente dominio={tractor.dominio} />}
      </div>

      <div className="llave-cuerpo">
        <div className="llave-izq">
          {!est ? (
            <p className="llave-estado">En portería</p>
          ) : est.afuera ? (
            <p className={`llave-estado afuera${demorada(est) || conf?.alerta ? " demorada" : ""}`}>
              La tiene <b>{nombreDe(est.persona)}</b>
              {conf && (
                <span className={`llave-conf ${conf.tipo}`} data-testid="llave-conf">
                  {conf.texto}
                </span>
              )}
              <span className="sub">
                desde el {horaCorta(est.desde)} · {hace(est.desde)}
              </span>
            </p>
          ) : (
            <p className="llave-estado">
              En portería
              <span className="sub">
                la trajo {nombreDe(est.persona)} el {horaCorta(est.desde)}
              </span>
            </p>
          )}

          <div className="vig-panel llave-form">
            <div className="llave-tipo" role="group" aria-label="Movimiento">
              {[
                [RETIRA, "Entrega"],
                [ENTREGA, "Recepción"],
              ].map(([t, rotulo]) => (
                <button
                  key={t}
                  type="button"
                  className={`btn llave-tipo-op${toca === t ? " activo" : ""}`}
                  aria-pressed={toca === t}
                  onClick={() => setTipo(t)}
                >
                  {rotulo}
                </button>
              ))}
            </div>
            {yo ? (
              <p className="vig-campo llave-yo" data-testid="llave-yo">
                <span>{toca === RETIRA ? "Te la llevás" : "La traés"}</span>
                <b>{nombreDe(yo)}</b>
              </p>
            ) : (
              <>
                <label className="vig-campo">
                  <span>{toca === RETIRA ? "Se la lleva" : "La trae"}</span>
                  <Select
                    inputId="llave-persona"
                    options={opcionesPersonas}
                    value={elegida(opcionesPersonas, persona)}
                    onChange={(o) => setPersona(o?.value || null)}
                    placeholder="Buscar persona"
                    isClearable
                  />
                </label>
                <label className="vig-campo">
                  <span>Sereno</span>
                  <Select
                    inputId="llave-sereno"
                    options={opcionesSerenos}
                    value={elegida(opcionesSerenos, operador)}
                    onChange={(o) => setOperador(o?.value || null)}
                    placeholder="Buscar sereno"
                  />
                </label>
              </>
            )}
            <label className="vig-campo ancho">
              <span>Detalle</span>
              <input
                type="text"
                value={detalle}
                onChange={(e) => setDetalle(e.target.value)}
                autoComplete="off"
              />
            </label>
            <button
              type="button"
              className={`btn llave-boton ${toca === RETIRA ? "retira" : "devuelve"}`}
              disabled={!listo || guardando}
              onClick={anotar}
            >
              {guardando
                ? "Anotando…"
                : toca === RETIRA
                  ? yo
                    ? "Me la llevo"
                    : "Se la lleva"
                  : yo
                    ? "La devuelvo"
                    : "La devuelve"}
            </button>
          </div>

          {resultado && (
            <p className={`panel-aviso ${resultado.ok ? "suave" : ""}`} role="status">
              {resultado.ok
                ? `${resultado.tipo === RETIRA ? "Se la llevó" : "La devolvió"} ${nombreDe(resultado.persona)} · ${horaCorta(resultado.cuando)}`
                : "No se pudo guardar"}
            </p>
          )}
        </div>

        <aside className="llave-qr" data-testid="llave-qr">
          <QrLlave
            texto={urlLlave(interno, tractor?.dominio)}
            tamano={168}
            titulo={`Llave del ${interno}`}
          />
          <b className="llave-qr-int">{interno}</b>
          {tractor?.dominio && <small className="llave-qr-pat">{tractor.dominio}</small>}
          <Link className="tk-chip" to={rutaEtiquetas(interno)}>
            Imprimir
          </Link>
        </aside>
      </div>

      <p className="llave-volver">
        <Link to="/llaves">Todas las llaves</Link>
      </p>
    </div>
  );
};

export default Llave;
